import { useTranslation } from 'react-i18next';
import type {
  CountStatus,
  OperationStatus,
  OperationType,
  TransferStatus,
  TransferMode,
} from '@lightbm/shared';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { OperationStatusBadge } from '@/features/operations/parts';
import { cn } from '@/lib/cn';

const TRANSFER_TONES: Record<TransferStatus, BadgeTone> = {
  draft: 'draft',
  sent: 'warning',
  partially_received: 'info',
  received: 'success',
  cancelled: 'neutral',
};

/** "Sent", "Partly received", "Received"; a one-step move reads "Done in one step". */
export function TransferStatusBadge({
  status,
  mode,
}: {
  status: TransferStatus;
  mode?: TransferMode;
}) {
  const { t } = useTranslation('transfers');
  return (
    <Badge
      tone={TRANSFER_TONES[status]}
      className={cn('whitespace-nowrap', status === 'cancelled' && 'line-through')}
    >
      {status === 'received' && mode === 'direct' ? t('status.direct') : t(`status.${status}`)}
    </Badge>
  );
}

const COUNT_TONES: Record<CountStatus, BadgeTone> = {
  draft: 'draft',
  in_progress: 'warning',
  review: 'reversed',
  validated: 'success',
  cancelled: 'neutral',
};

export function CountStatusBadge({ status }: { status: CountStatus }) {
  const { t } = useTranslation('counts');
  return (
    <Badge tone={COUNT_TONES[status]} className="whitespace-nowrap">
      {t(`status.${status}`)}
    </Badge>
  );
}

/** The status of any document, as its own screens show it (global search). */
export function DocumentStatusBadge({
  type,
  status,
}: {
  type?: OperationType | 'transfer' | 'count';
  status: OperationStatus | TransferStatus | CountStatus;
}) {
  if (type === 'transfer') return <TransferStatusBadge status={status as TransferStatus} />;
  if (type === 'count') return <CountStatusBadge status={status as CountStatus} />;
  return <OperationStatusBadge status={status as OperationStatus} />;
}
